// Shared view fragments — the notes thread and editors strip appear in BOTH
// the review view (owner) and the shared view (token = key), so they live
// here once. Each returns its unsubscribe fn.
import { watchNotes, addNote, watchMembers } from '../lib/firestore.js';
import { isGuest } from '../lib/firebase.js';

/** True on iPhone/iPad/iPod. iPadOS 13+ reports a Mac user-agent but keeps
 * touch points — real Macs report maxTouchPoints 0. */
export function isIOS() {
  if (/iPad|iPhone|iPod/.test(navigator.userAgent)) return true;
  return /Mac/.test(navigator.userAgent) && navigator.maxTouchPoints > 1;
}

/** Header identity — guest → "Guest" + "Sign in"; real user → account icon
 * (no text, so the top menu stays one line on narrow screens). Rendered into
 * a placeholder div in each view's header so the wiring lives here once.
 * Guests never get a Sign out button: signing out orphans their
 * (device-local) data permanently. */
const USER_ICON = `<svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>`;

export function renderHeaderIdentity(el, { user, navigate }) {
  if (!user) return; // leave placeholder — views may pre-render a sign-in button
  if (isGuest(user)) {
    el.innerHTML = `
      <span class="who">Guest</span>
      <button class="btn ghost" id="sign-in">Sign in to keep your work</button>`;
    el.querySelector('#sign-in').addEventListener('click', () => {
      // Read the hash at CLICK time (not render time): the map rewrites it as
      // you explore (camera, layers, selected pin), and signing in should come
      // back to that exact view rather than to a fresh one. See the `next`
      // handling in views/login.js.
      const here = location.hash.replace(/^#\/?/, '') || 'map';
      navigate(`#/login?next=${encodeURIComponent(here)}`);
    });
    return;
  }
  // The email is the account entry, shown as an icon (sign-out lives on the
  // account page).
  el.innerHTML = `
    <a class="who" href="#/account" title="${escapeAttr(user.email || 'Account')}" aria-label="Account">${USER_ICON}</a>`;
}

/** Notes thread (form + live list) rendered into `el`. Returns unsubscribe.
 * Comment-section styling: no heading, avatar per author, newest first. The
 * composer is just an input + send icon (no "Add" button text). Thread is
 * keyed by property id (S4: notes live on properties/{pid}/notes). */
const SEND_ICON = `<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>`;

export function renderNotesThread(el, { pid, user, disabled }) {
  el.innerHTML = `
    <form id="note-form" class="note-form">
      <input type="text" id="note-input" placeholder="${disabled ? 'Sign in to add notes' : 'Add a comment…'}"
        ${disabled ? 'disabled' : ''} autocomplete="off" />
      <button class="note-send" id="note-btn" ${disabled ? 'disabled' : ''} aria-label="Send comment">${SEND_ICON}</button>
    </form>
    <div id="notes"><div class="muted">Loading comments…</div></div>`;

  const notesEl = el.querySelector('#notes');
  const form = el.querySelector('#note-form');
  const input = el.querySelector('#note-input');
  const btn = el.querySelector('#note-btn');

  // Avatar colour is stable per author (hash of name → palette).
  const AVATAR_COLORS = ['#0f766e', '#1d4ed8', '#b45309', '#be185d', '#4f46e5', '#059669'];
  const avatarColor = (name) =>
    AVATAR_COLORS[[...(name || '?')].reduce((a, c) => a + c.charCodeAt(0), 0) % AVATAR_COLORS.length];
  const ts = (n) => {
    const t = n.created_at;
    return t ? (typeof t.toMillis === 'function' ? t.toMillis() : new Date(t).getTime()) : 0;
  };

  const unsubNotes = watchNotes(pid, (notes) => {
    if (!notes.length) {
      notesEl.innerHTML = ''; // no empty-state filler — the composer is the entry point
      return;
    }
    // Newest first — comment sections read top-down.
    const sorted = [...notes].sort((a, b) => ts(b) - ts(a));
    notesEl.innerHTML = sorted
      .map((n) => {
        const name = n.author_name || 'Guest';
        const initial = (name[0] || '?').toUpperCase();
        return `
        <div class="note-item">
          <span class="note-avatar" style="background:${avatarColor(name)}">${escapeHtml(initial)}</span>
          <div class="note-body">
            <div class="note-meta"><strong>${escapeHtml(name)}</strong><span class="muted">${escapeHtml(fmtTime(n.created_at))}</span></div>
            <div class="note-text">${escapeHtml(n.text)}</div>
          </div>
        </div>`;
      })
      .join('');
  });

  if (!disabled && user) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const text = input.value.trim();
      if (!text) return;
      btn.disabled = true;
      try {
        await addNote(pid, {
          authorUid: user.uid,
          authorName: user.displayName || user.email || 'Guest',
          text,
        });
        input.value = '';
        input.focus();
      } catch (err) {
        console.warn('note failed', err);
        input.placeholder = 'Note failed — try again';
      }
      btn.disabled = false;
    });
  }
  return unsubNotes;
}

/** Editors strip — names + joined dates; owner gets a remove (×) per member. */
export function renderEditorsStrip(el, { token, isOwner, onRemove }) {
  el.innerHTML = '<div class="muted">Loading editors…</div>';
  return watchMembers(token, (members) => {
    if (!members.length) {
      el.innerHTML = '<div class="muted">No editors yet — share the link.</div>';
      return;
    }
    el.innerHTML = members
      .map(
        (m) => `
        <div class="member-item">
          <span>${escapeHtml(m.display_name || 'Guest')}</span>
          <span class="muted">joined ${escapeHtml(fmtTime(m.joined_at))}</span>
          ${isOwner ? `<button class="remove-member" data-uid="${escapeAttr(m.id)}" title="Remove editor">×</button>` : ''}
        </div>`,
      )
      .join('');
    if (isOwner) {
      el.querySelectorAll('.remove-member').forEach((b) =>
        b.addEventListener('click', () => onRemove && onRemove(b.dataset.uid)),
      );
    }
  });
}

export const STATUSES = ['potential', 'reviewing', 'shortlist', 'rejected', 'purchased'];
export const STATUS_LABELS = {
  potential: 'Potential',
  reviewing: 'Reviewing',
  shortlist: 'Shortlist',
  rejected: 'Rejected',
  purchased: 'Purchased',
};

/** Just the domain (trademe.co.nz) of a URL — no scheme, no path. */
export function sourceDomain(url) {
  if (!url) return 'no URL';
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    /* non-URL text — show as-is */
    return url;
  }
}

/** Source URL shown as just its domain (trademe.co.nz), linking out.
 * No URL → empty string; callers hide the surrounding row. */
export function sourceLinkHtml(url) {
  if (!url) return '';
  return `<a class="source-link" href="${escapeAttr(url)}" target="_blank" rel="noopener noreferrer">${escapeHtml(sourceDomain(url))}</a>`;
}

export function fmtTime(t) {
  if (!t) return '';
  const ms = typeof t.toMillis === 'function' ? t.toMillis() : new Date(t).getTime();
  return isNaN(ms) ? '' : new Date(ms).toLocaleString();
}

export function escapeHtml(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
}
export function escapeAttr(s) {
  return escapeHtml(s);
}
